import { useCallback, useEffect, useMemo, useRef } from "react";

import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { scrollIntoView } from "../../utils/scrollIntoView";
import type { VttSegment } from "./useTranscript";

interface UseAutoScrollOptions {
  currentTime: number;
  segments: VttSegment[];
  isAutoScrolling: boolean;
  isAutoScrollEnabled: boolean;
  options?: {
    onUserScroll?: () => void;
    refreshTrigger?: { value: string; timeout: number };
    excludeFromUserInteractionRef?: React.RefObject<Element>;
    scrollContainerRef?: React.RefObject<HTMLElement>;
  };
}

interface UseAutoScrollResult {
  activeSegmentId: string | null;
  scrollToSegment: (segmentId: string) => void;
  handleUserScroll: () => void;
}

export const useAutoScroll = ({
  currentTime,
  segments,
  isAutoScrolling,
  isAutoScrollEnabled,
  options,
}: UseAutoScrollOptions): UseAutoScrollResult => {
  const {
    onUserScroll,
    refreshTrigger,
    excludeFromUserInteractionRef,
    scrollContainerRef,
  } = options ?? {};
  const lastAutoScrollTime = useRef<number>(0);
  const userInteractingRef = useRef(false);
  const prevRefreshTriggerRef = useRef<unknown>(null);
  const hasScrolledOnMount = useRef(false);
  const { value: refreshTriggerValue, timeout: refreshTriggerTimeout } =
    refreshTrigger ?? {};
  const isDesktop = useIsDesktopViewport();

  // Detect manual scrolling
  useEffect(() => {
    if (excludeFromUserInteractionRef?.current) return;

    const setUser = (event: Event) => {
      if (
        excludeFromUserInteractionRef?.current &&
        event.target instanceof Node
      ) {
        if (
          excludeFromUserInteractionRef.current.contains(event.target) ||
          excludeFromUserInteractionRef.current === event.target
        ) {
          return;
        }
      }
      userInteractingRef.current = true;
      setTimeout(() => (userInteractingRef.current = false), 500);
    };
    if (!isDesktop) {
      window.addEventListener("touchstart", setUser);
    }
    window.addEventListener("wheel", setUser);

    return () => {
      if (!isDesktop) {
        window.removeEventListener("touchstart", setUser);
      }
      window.removeEventListener("wheel", setUser);
    };
  }, []);

  const activeSegmentId = useMemo(() => {
    if (!segments.length) return null;

    const isWithin = (segment: VttSegment) =>
      currentTime >= segment.startTime && currentTime <= segment.endTime;

    const isBeforeOrAt = (segment: VttSegment) =>
      segment.startTime <= currentTime;

    const currentSegment = segments.find(isWithin);
    if (currentSegment) return currentSegment.id;

    const previousSegment = segments.findLast(isBeforeOrAt);
    if (previousSegment) return previousSegment.id;

    return segments[0].id;
  }, [currentTime, segments]);

  const scrollToSegment = useCallback((segmentId: string) => {
    const element = document.getElementById(segmentId);
    if (element) {
      const container = scrollContainerRef?.current || window;
      scrollIntoView(element, {
        duration: 800,
        block: "center",
        container,
      });
    }
  }, []);

  // Scroll to current segment on mount
  useEffect(() => {
    if (!isAutoScrollEnabled || !activeSegmentId || hasScrolledOnMount.current)
      return;

    if (activeSegmentId) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        scrollToSegment(activeSegmentId);
        hasScrolledOnMount.current = true;
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isAutoScrollEnabled, activeSegmentId]);

  // Auto-scroll to active segment
  useEffect(() => {
    if (!isAutoScrolling || !activeSegmentId || !isAutoScrollEnabled) return;

    // Prevent rapid scrolling by adding a small delay
    const now = Date.now();
    if (now - lastAutoScrollTime.current < 200) return;

    scrollToSegment(activeSegmentId);
    lastAutoScrollTime.current = now;
  }, [activeSegmentId, isAutoScrolling, isAutoScrollEnabled, scrollToSegment]);

  // Handle refresh trigger changes
  useEffect(() => {
    if (!isAutoScrolling || !activeSegmentId || refreshTrigger === undefined)
      return;

    const hasChanged = prevRefreshTriggerRef.current !== refreshTriggerValue;
    prevRefreshTriggerRef.current = refreshTriggerValue;

    if (!hasChanged) return;

    const timer = setTimeout(() => {
      const element = document.getElementById(activeSegmentId);
      if (element) {
        element.offsetHeight;
        scrollToSegment(activeSegmentId);
      }
    }, refreshTriggerTimeout);

    return () => clearTimeout(timer);
  }, [
    refreshTriggerValue,
    isAutoScrolling,
    activeSegmentId,
    scrollToSegment,
    refreshTriggerTimeout,
  ]);

  const handleUserScroll = useCallback(() => {
    if (userInteractingRef.current && isAutoScrolling) {
      onUserScroll?.();
    }
  }, [onUserScroll, isAutoScrolling]);

  return {
    activeSegmentId,
    scrollToSegment,
    handleUserScroll,
  };
};
